// ── Light-theme presentational primitives for Platform Journey Tool ─────────
//
// Forked from testing-ground/components/shared.tsx rather than reusing it
// directly — that file is Testing Ground's own dark theme, and editing it
// would re-theme Testing Ground too. This file is the Platform Journey Tool's
// own equivalent, styled to match Operations Center's actual light look.
import { useState } from 'react'
import { ChevronDown, Check, Copy } from 'lucide-react'

export const inputCls = 'w-full bg-white border border-[#cbd5e1] rounded-[7px] px-2.5 py-[6px] text-[12px] text-[#1e293b] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#6366f1]/60 font-mono'
export const labelCls = 'block text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-1'

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-2">{children}</p>
}

export function ResultBadge({ result }: { result: string }) {
  const r = result.toUpperCase()
  const style = r === 'MATCH' || r === 'CLEAR' || r === 'PASS'
    ? { bg: '#dcfce7', color: '#15803d' }
    : r === 'NO_MATCH' || r === 'FAIL'
    ? { bg: '#fee2e2', color: '#b91c1c' }
    : r === 'REFER' || r === 'REVIEW' || r === 'MEDIUM'
    ? { bg: '#ffedd5', color: '#c2410c' }
    : { bg: '#f1f5f9', color: '#475569' }
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: style.bg, color: style.color }}>
      {result}
    </span>
  )
}

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] transition-colors"
      style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#64748b' }}
    >
      {copied ? <Check size={9} className="text-emerald-600" /> : <Copy size={9} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

export interface ComboOption {
  value: string
  label?: string
}

export function ComboSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
  style,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  options: ComboOption[]
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)

  const filtered = options.filter(o =>
    !value || o.value.toLowerCase().includes(value.toLowerCase()) || (o.label && o.label.toLowerCase().includes(value.toLowerCase()))
  )

  return (
    <div className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className={className ?? inputCls}
          style={style}
          disabled={disabled}
        />
        {!disabled && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#f1f5f9] transition-colors"
          style={{ color: '#64748b' }}
          tabIndex={-1}
        >
          <ChevronDown size={11} />
        </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-lg overflow-hidden overflow-y-auto"
          style={{ background: '#ffffff', border: '1px solid #e5e7eb', maxHeight: 180 }}
        >
          {filtered.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false) }}
              className="w-full text-left px-3 py-1.5 text-[11px] font-mono hover:bg-[#f8fafc] transition-colors flex items-center gap-2"
              style={{ color: value === o.value ? '#4f46e5' : '#475569' }}
            >
              <span className="font-semibold">{o.value}</span>
              {o.label && <span style={{ color: '#64748b' }}>— {o.label}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function RawJsonCollapsible({ data, label = 'Raw JSON Response' }: { data: unknown; label?: string }) {
  const [open, setOpen] = useState(false)
  const json = JSON.stringify(data, null, 2)
  return (
    <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#f8fafc]"
        style={{ background: '#ffffff' }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">{label}</span>
        <ChevronDown size={11} className="text-[#64748b]" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div className="p-3" style={{ background: '#f8fafc', borderTop: '1px solid #e5e7eb', maxHeight: 320, overflowY: 'auto' }}>
          <div className="flex justify-end mb-2">
            <CopyButton text={json} />
          </div>
          <pre className="text-[10px] text-[#475569] font-mono whitespace-pre-wrap break-all">{json}</pre>
        </div>
      )}
    </div>
  )
}
